import React, { useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Menu } from 'primereact/menu';
import { ChevronDown, CalendarDays, X, Calendar as LucideCalendar } from 'lucide-react';

interface DateRangePickerProps {
    value: (Date | null)[] | null;
    onChange: (range: (Date | null)[]) => void;
}

export const DateRangePicker = ({ value, onChange }: DateRangePickerProps) => {
    const menuRef = useRef<Menu>(null);
    const calendarRef = useRef<any>(null);

    const getRanges = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        return {
            today: [today, end],
            week: (() => {
                const start = new Date(today);
                const diff = start.getDate() - start.getDay() + (start.getDay() === 0 ? -6 : 1);
                return [new Date(start.setDate(diff)), end];
            })(),
            month: [new Date(today.getFullYear(), today.getMonth(), 1), end],
        };
    };

    const formatDateLabel = () => {
        if (!value || !value[0]) return "Tarih Seçin";
        const start = value[0].toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
        if (!value[1]) return `${start} - ...`;
        const end = value[1].toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
        return `${start} - ${end}`;
    };

    const moreActions = [
        { label: 'Bugün', className: 'md:hidden', command: () => onChange(getRanges().today) },
        { label: 'Bu Hafta', className: 'md:hidden', command: () => onChange(getRanges().week) },
        { label: 'Son 7 Gün', command: () => onChange([new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]) },
        { label: 'Son 30 Gün', command: () => onChange([new Date(new Date().setMonth(new Date().getMonth() - 1)), new Date()]) },
        { separator: true },
        { label: 'Takvimi Aç', icon: 'pi pi-calendar', command: () => calendarRef.current?.show() },
        { label: 'Temizle', icon: 'pi pi-trash', className: 'text-red-500', command: () => onChange([null, null]) },
    ];

    return (<div className="inline-flex items-center rounded-xl shadow-sm p-0.5 w-full md:w-auto borderx" 
             style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
            
            {/* 📱 Mobilde Gizlenen Hızlı Butonlar */}
            <div className="hidden md:flex items-center gap-0.5 pr-1" style={{ borderRight: '1px solid var(--border)' }}>
                {[
                    { label: 'Bugün', key: 'today' },
                    { label: 'Hafta', key: 'week' },
                    { label: 'Ay', key: 'month' }
                ].map((item) => (
                    <button
                        key={item.key}
                        onClick={() => onChange(getRanges()[item.key as keyof ReturnType<typeof getRanges>])}
                        className="px-2.5 py-1.5 text-[11px] font-bold transition-all rounded-lg"
                        style={{ color: 'var(--text)', opacity: 0.7 }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.color = 'var(--primary)';
                            e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary) 10%, transparent)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.color = 'var(--text)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {/* 🗓 Ana Alan: Seçili Tarih */}
            <div className="relative flex items-center flex-1 min-w-0">
                <button
                    onClick={() => calendarRef.current?.show()}
                    className="flex items-center gap-2 px-3 py-1.5 w-full rounded-lg transition-colors"
                    style={{ color: 'var(--text)' }}
                >
                    <CalendarDays size={14} style={{ color: 'var(--primary)' }} className="shrink-0" />
                    <span className="text-[11px] font-bold truncate">
                        {formatDateLabel()}
                    </span>
                    <ChevronDown size={12} className="ml-auto shrink-0" style={{ opacity: 0.4 }} />
                </button>

  <Calendar
    ref={calendarRef}
    value={value as any}
    onChange={(e) => {
        const selectedDates = e.value as (Date | null)[];
        
        // Eğer aralık tamamlandıysa (hem 1. hem 2. tarih seçildiyse)
        if (selectedDates && selectedDates[0] && selectedDates[1]) {
           const start = new Date(selectedDates[0]);
const end = new Date(selectedDates[1]);

onChange([start, end]);
            // Karartı (mask) kalmaması için takvimi temiz bir şekilde kapat
            setTimeout(() => {
                calendarRef.current?.hide();
            }, 50);
        } else {
            // Henüz sadece ilk tarih seçildiyse (range tamamlanmadıysa) 
            // olduğu gibi gönder. (Parent'taki Yöntem 1 bunu API'ye göndermeyecek)
            onChange(selectedDates);
        }
    }}
    selectionMode="range"
    readOnlyInput
    className="absolute opacity-0 pointer-events-none w-0"
    touchUI
/>
            </div>

            {/* ⚙️ Menü ve Silme Alanı */}
            <div className="flex items-center pl-1" style={{ borderLeft: '1px solid var(--border)' }}>
                {value?.[0] && (
                    <button 
                        onClick={() => onChange([null, null])}
                        className="p-1.5 rounded-lg transition-all"
                        style={{ color: 'var(--red)', opacity: 0.6 }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--red) 10%, transparent)'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <X size={14} />
                    </button>
                )}
                
                <Menu model={moreActions} popup ref={menuRef} style={{ background: 'var(--dropdown-bg)', border: '1px solid var(--border)' }} />
                <button
                    onClick={(e) => menuRef.current?.toggle(e)}
                    className="p-1.5 transition-colors"
                    style={{ color: 'var(--text)', opacity: 0.5 }}
                >
                    <LucideCalendar size={14} />
                </button>
            </div>
        </div>
    );
};